/* ============================================================================
   BRITTEN CUSTOM: Cart 4a coordinator (work package 1)
   assets/britten-bag.js. Plain JavaScript, no Liquid.

   Loaded only by the Cart 4a branch of sections/cart__main.liquid (flag
   britten_cart_4a, cart template only). It replaces, under the flag, the two
   cart-only scripts that used to keep the bag honest
   (snippets/britten-length-surcharge-cart.liquid and an add-on guard since
   removed). It makes no global fetch or XHR wrapper and never reloads the page.

   WHAT IT DOES
   - One serial queue of cart mutations, by line KEY, against the locale-prefixed
     Cart AJAX API ({root}cart/change.js, update.js, add.js), every URL absolute.
   - After every change it re-reads /cart.js and reconciles the bag:
       a veil with no extra-length surcharge   -> the surcharge is ADDED (the veil
                                                  is never deleted)
       surcharge quantity                      -> always overage cm x veil quantity
       a surcharge or an option extra with no parent -> removed
       removing a group                        -> ONE cart/update.js with every
                                                  line of the group set to 0
                                                  (children, surcharge, parent),
                                                  so it goes whole or not at all
   - Checkout stays locked (data-britten-bag-locked) until the bag is reconciled
     and on screen. On a failure it stays locked and shows the error with a
     retry and a link to the full bag.
   - It swaps the server-rendered bag section in place (Section Rendering API,
     bundled into change.js where it can be). It never formats or prints money.

   ORDER DATA SAFETY
   The coordinator never renames, reformats or translates a line item property
   or a cart attribute. The surcharge add payload is byte-identical to the one
   addMissingSurcharges used to send. _parent_product_title and
   _parent_product_handle come from an UN-prefixed /cart.js read, so they stay
   English on a translated page. Display text is never read from /cart.js.

   FILES. This file is the CORE: the pure logic and the engine (no DOM). It loads
   on every page where the coordinator runs and is shared by the two views:
   assets/britten-bag-page.js (the bag page) and assets/britten-bag-drawer.js (the
   drawer and the add-to-bag interception, every page except the cart).

   FAST ADD (theme setting britten_bag_fast_add, default on; October 2026). The drawer's
   add used to run five requests in series before anything showed. With the setting on,
   assets/britten-bag-drawer.js opens the drawer at once and the add costs two:
     - the add POST carries sections=<drawer> and sections_url (addRaw with
       {bundle:true}), so the drawer's HTML comes back with the add and is swapped in
       without a separate GET;
     - the "after" cart read is handed to the reconcile as its first pass
       (settleAdd), instead of being read a second time. The loop still re-reads after
       anything it changes, and the seed is dropped if the engine sent any cart
       mutation since that read began;
     - if the reconcile changes nothing the HTML that came with the add is kept; if it
       adds a surcharge the section is fetched again, as before.
   Nothing about the surcharge add, its body or planReconcile changed. With the setting
   off runAddFlow runs the old sequence exactly (no `fast` dep is passed).

   TESTS: scripts/bag-coordinator-test.js loads this file in node and drives the
   pure logic and the engine with a fake cart server. Nothing in this file touches
   the DOM.
   ========================================================================== */
(function (root) {
  'use strict';

  /* ======================================================================
     Constants
     ====================================================================== */

  /* NEW BEHAVIOUR, AWAITING THE OWNER'S CONFIRMATION. When a parent line's
     quantity changes, scale each option extra's quantity with it.
     Today a child keeps its quantity. OFF keeps today's behaviour. */
  var SCALE_IO_CHILDREN_WITH_PARENT = false;

  var MAX_RECONCILE_PASSES = 4;
  var REQUEST_TIMEOUT_MS = 20000;
  var UNRESOLVED_AT_START = true;

  var P_BUNDLE = '_length_bundle_id';
  var P_HAS_SURCHARGE = '_has_length_surcharge';
  var P_OVERAGE = '_overage_cm';
  var P_SURCHARGE_VARIANT = '_surcharge_variant_id';
  var P_PARENT_HANDLE = '_parent_product_handle';
  var P_EXTRA_LENGTH = '_extra_length';
  var P_IO_PARENT = '_io_order_group';
  var P_IO_CHILD = '_io_parent_order_group';
  var SURCHARGE_HANDLE_PREFIX = 'extra-veil-length';

  /* ======================================================================
     Pure logic (no DOM, no network)
     ====================================================================== */

  /* /cart.js gives properties as an object; some code paths give pairs. */
  function getProp(item, key) {
    var props = item && item.properties;
    if (!props) { return null; }
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i] && props[i][0] === key) { return props[i][1]; }
      }
      return null;
    }
    var v = props[key];
    return (v === undefined || v === null || v === '') ? null : v;
  }

  /* Does the line carry the custom-length property (key contains CUSTOM LENGTH, with
     a value)? The product page only writes it while the length is custom. */
  function hasCustomLengthProp(item) {
    var props = item && item.properties;
    if (!props) { return false; }
    var i;
    if (Array.isArray(props)) {
      for (i = 0; i < props.length; i++) {
        if (props[i] && String(props[i][0]).indexOf('CUSTOM LENGTH') !== -1 && props[i][1]) { return true; }
      }
      return false;
    }
    for (var k in props) {
      if (Object.prototype.hasOwnProperty.call(props, k) && k.indexOf('CUSTOM LENGTH') !== -1 && props[k]) { return true; }
    }
    return false;
  }

  /* A veil owes an extra-length charge ONLY when it says so consistently:
       _has_length_surcharge = Yes, a bundle id, the custom-length property, and an
       overage that is not explicitly zero or negative.
     A line carrying leftovers (the flag and an old bundle id, but no custom-length
     property, or a zero overage) is a standard veil as far as reconciliation goes: it
     is never given a charge and never deleted. A missing or unreadable overage WITH
     the custom-length property is still custom: the money is then ambiguous, and
     planReconcile reports it instead of guessing. */
  function owesLengthSurcharge(item) {
    if (getProp(item, P_HAS_SURCHARGE) !== 'Yes' || !getProp(item, P_BUNDLE)) { return false; }
    if (!hasCustomLengthProp(item)) { return false; }
    var raw = getProp(item, P_OVERAGE);
    if (raw !== null && !isNaN(parseInt(raw, 10)) && parseInt(raw, 10) <= 0) { return false; }
    return true;
  }

  function isSurchargeLine(item) {
    if (getProp(item, P_PARENT_HANDLE)) { return true; }
    return !!(item && item.handle && String(item.handle).indexOf(SURCHARGE_HANDLE_PREFIX) === 0);
  }

  function isIoChildLine(item) {
    return !!getProp(item, P_IO_CHILD);
  }

  /* Group the cart by the stored link properties. Never by title, handle alone
     or position. A child with no parent in the bag is an orphan. */
  function analyse(cart) {
    var items = (cart && cart.items) || [];
    var out = {
      groups: [],
      byKey: {},
      orphanSurcharges: [],
      orphanIoChildren: []
    };
    var veilByBundle = {};
    var parentByIoGroup = {};
    var surcharges = [];
    var ioChildren = [];
    var i;

    for (i = 0; i < items.length; i++) {
      var item = items[i];
      if (isSurchargeLine(item)) {
        surcharges.push(item);
      } else if (isIoChildLine(item)) {
        ioChildren.push(item);
      } else {
        var group = {
          key: item.key,
          item: item,
          bundleId: getProp(item, P_BUNDLE),
          ioGroup: getProp(item, P_IO_PARENT),
          customLength: owesLengthSurcharge(item),
          sharedBundle: false,
          surcharges: [],
          ioChildren: []
        };
        out.groups.push(group);
        out.byKey[item.key] = group;
        if (group.customLength) {
          if (!veilByBundle[group.bundleId]) {
            veilByBundle[group.bundleId] = group;
          } else {
            /* A second custom veil under the SAME bundle id: its charge cannot be told
               from the first one's. Reported, never repeated and never deleted. */
            group.sharedBundle = true;
          }
        }
        if (group.ioGroup && !parentByIoGroup[group.ioGroup]) {
          parentByIoGroup[group.ioGroup] = group;
        }
      }
    }

    for (i = 0; i < surcharges.length; i++) {
      var sc = surcharges[i];
      var bundle = getProp(sc, P_BUNDLE);
      if (bundle && veilByBundle[bundle]) {
        veilByBundle[bundle].surcharges.push(sc);
      } else {
        out.orphanSurcharges.push(sc);
      }
    }
    for (i = 0; i < ioChildren.length; i++) {
      var child = ioChildren[i];
      var parent = parentByIoGroup[getProp(child, P_IO_CHILD)];
      if (parent) {
        parent.ioChildren.push(child);
      } else {
        out.orphanIoChildren.push(child);
      }
    }
    return out;
  }

  /* The header and subtitle count: the quantities of primary lines. Surcharge
     units (centimetres) and option extras are not pieces. The
     Liquid in snippets/britten-bag-groups.liquid uses the same rule. */
  function displayCount(cart) {
    var items = (cart && cart.items) || [];
    var n = 0;
    for (var i = 0; i < items.length; i++) {
      if (isSurchargeLine(items[i]) || isIoChildLine(items[i])) { continue; }
      n += items[i].quantity;
    }
    return n;
  }

  function parseCm(value) {
    var m = /(\d+)/.exec(String(value === undefined || value === null ? '' : value));
    return m ? parseInt(m[1], 10) : NaN;
  }

  /* Overage in whole centimetres for a custom-length veil group. Reads the
     veil's _overage_cm; falls back to the paired surcharge's _extra_length
     (+40cm). NaN when neither says. */
  function overageOf(group) {
    var n = parseInt(getProp(group.item, P_OVERAGE), 10);
    if (n > 0) { return n; }
    for (var i = 0; i < group.surcharges.length; i++) {
      var e = parseCm(getProp(group.surcharges[i], P_EXTRA_LENGTH));
      if (e > 0) { return e; }
    }
    return NaN;
  }

  /* Surcharge quantity is ALWAYS overage cm x veil quantity. */
  function surchargeQuantity(overageCm, veilQuantity) {
    return overageCm * veilQuantity;
  }

  /* The body posted to cart/add.js for a missing surcharge. Byte-identical to
     the old addMissingSurcharges: same keys, same order, same values. The
     handle and title come from the un-prefixed /cart.js read. */
  function buildSurchargeAddPayload(veilItem, overageCm) {
    var props = veilItem.properties || {};
    return {
      id: parseInt(getProp(veilItem, P_SURCHARGE_VARIANT) || props[P_SURCHARGE_VARIANT], 10),
      quantity: surchargeQuantity(overageCm, veilItem.quantity),
      properties: {
        '_length_bundle_id': getProp(veilItem, P_BUNDLE),
        '_parent_product_handle': veilItem.handle || '',
        '_parent_product_title': veilItem.product_title || '',
        '_extra_length': '+' + overageCm + 'cm',
        '_band': ''
      }
    };
  }

  /* What the bag needs, read from the cart as it is NOW (no earlier snapshot).
     Returns { steps, problems }.
       steps    ordered mutations: { op: 'change', key, quantity, reason } or
                { op: 'add', payload, reason, key }
       problems things that cannot be fixed from here (a veil whose surcharge
                data is incomplete). The bag stays locked for these. */
  function planReconcile(cart) {
    var a = analyse(cart);
    var steps = [];
    var problems = [];
    var i;

    for (i = 0; i < a.orphanIoChildren.length; i++) {
      steps.push({ op: 'change', key: a.orphanIoChildren[i].key, quantity: 0, reason: 'io-orphan' });
    }
    for (i = 0; i < a.orphanSurcharges.length; i++) {
      steps.push({ op: 'change', key: a.orphanSurcharges[i].key, quantity: 0, reason: 'surcharge-orphan' });
    }

    for (i = 0; i < a.groups.length; i++) {
      var g = a.groups[i];
      if (!g.customLength) { continue; }
      if (g.sharedBundle) {
        problems.push({ code: 'bundle-shared', key: g.key });
        continue;
      }
      var overage = overageOf(g);
      if (!(overage > 0)) {
        problems.push({ code: 'overage-unknown', key: g.key });
        continue;
      }
      var expected = surchargeQuantity(overage, g.item.quantity);
      if (g.surcharges.length === 0) {
        var variantId = parseInt(getProp(g.item, P_SURCHARGE_VARIANT), 10);
        var overageFromVeil = parseInt(getProp(g.item, P_OVERAGE), 10);
        if (!(variantId > 0) || !(overageFromVeil > 0)) {
          problems.push({ code: 'surcharge-data-missing', key: g.key });
          continue;
        }
        steps.push({
          op: 'add',
          key: g.key,
          reason: 'surcharge-missing',
          payload: buildSurchargeAddPayload(g.item, overageFromVeil)
        });
      } else {
        for (var j = 0; j < g.surcharges.length; j++) {
          var s = g.surcharges[j];
          if (j > 0) {
            steps.push({ op: 'change', key: s.key, quantity: 0, reason: 'surcharge-duplicate' });
          } else if (s.quantity !== expected) {
            steps.push({ op: 'change', key: s.key, quantity: expected, reason: 'surcharge-quantity' });
          }
        }
      }
    }
    return { steps: steps, problems: problems };
  }

  /* Remove a group: children first, then the surcharge, then the parent. A line
     that is itself an orphan child is removed alone. Never touches a standalone
     accessory or another veil's lines. */
  function planRemoveGroup(cart, key) {
    var a = analyse(cart);
    var g = a.byKey[key];
    var steps = [];
    var i;
    if (!g) {
      var items = (cart && cart.items) || [];
      for (i = 0; i < items.length; i++) {
        if (items[i].key === key) {
          steps.push({ op: 'change', key: key, quantity: 0, reason: 'remove-line' });
        }
      }
      return steps;
    }
    for (i = 0; i < g.ioChildren.length; i++) {
      steps.push({ op: 'change', key: g.ioChildren[i].key, quantity: 0, reason: 'remove-io-child' });
    }
    for (i = 0; i < g.surcharges.length; i++) {
      steps.push({ op: 'change', key: g.surcharges[i].key, quantity: 0, reason: 'remove-surcharge' });
    }
    steps.push({ op: 'change', key: g.key, quantity: 0, reason: 'remove-parent' });
    return steps;
  }

  function scaleChildQuantity(childQuantity, oldParentQuantity, newParentQuantity) {
    if (!(oldParentQuantity > 0)) { return childQuantity; }
    return Math.max(1, Math.round(childQuantity * newParentQuantity / oldParentQuantity));
  }

  /* Change one group's quantity. The surcharge is NOT touched here: the
     reconcile pass that follows recomputes it as overage x the new quantity. */
  function planQuantityChange(cart, key, quantity, options) {
    var a = analyse(cart);
    var g = a.byKey[key];
    var steps = [];
    var items = (cart && cart.items) || [];
    var line = null;
    var i;
    for (i = 0; i < items.length; i++) {
      if (items[i].key === key) { line = items[i]; }
    }
    if (!line || line.quantity === quantity) { return steps; }
    steps.push({ op: 'change', key: key, quantity: quantity, reason: 'quantity', bundle: true });
    if (g && options && options.scaleIo) {
      for (i = 0; i < g.ioChildren.length; i++) {
        var scaled = scaleChildQuantity(g.ioChildren[i].quantity, line.quantity, quantity);
        if (scaled !== g.ioChildren[i].quantity) {
          steps.push({ op: 'change', key: g.ioChildren[i].key, quantity: scaled, reason: 'io-child-scale' });
        }
      }
    }
    return steps;
  }

  function normaliseRoot(rootUrl) {
    var r = (typeof rootUrl === 'string' && rootUrl) ? rootUrl : '/';
    if (r.charAt(0) !== '/' && !/^https?:\/\//i.test(r)) { r = '/' + r; }
    if (r.charAt(r.length - 1) !== '/') { r += '/'; }
    return r;
  }

  /* Every request URL is built ABSOLUTE: new URL(path, origin). A relative string
     is a valid fetch() argument, but any script that wraps fetch and parses its
     first argument with `new URL(arg)` throws "Failed to construct 'URL': Invalid
     URL" on one. An absolute URL parses everywhere. rootUrl may be "/", "/es-es/"
     or "/es-es" (or absolute). With no origin (a node test that did not give
     one) the path is returned as is. Never throws. */
  function buildUrl(rootUrl, path, origin, query) {
    var r = normaliseRoot(rootUrl);
    var rel = r + (path || '');
    if (query) { rel += '?' + query; }
    if (!origin) { return rel; }
    try {
      var u = new URL(rel, origin);
      return u.toString();
    } catch (e) {
      return rel;
    }
  }

  /* The cart as a comparable string: every line key and quantity, sorted. The
     page carries the same pairs in its updates[...] inputs, so the view can
     tell whether what is on screen is still the cart. */
  function cartSignature(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) { out.push(items[i].key + ':' + items[i].quantity); }
    return out.sort().join('|');
  }

  /* Wedding-veil units in the cart (the veil limit counts product_type
     "wedding veil"). The coordinator mirrors the gate's count so it can keep
     checkout held while the gate's own observer has not yet evaluated a freshly
     swapped section. The gate stays the authority; this only holds the door. */
  function veilUnits(cart) {
    var items = (cart && cart.items) || [];
    var n = 0;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].product_type || '').toLowerCase() === 'wedding veil') { n += items[i].quantity; }
    }
    return n;
  }

  /* Remove a whole group in ONE request: cart/update.js with every line key set
     to 0. Shopify applies one request as one cart operation, so the group goes
     entirely or not at all (the old three-request cascade could leave a veil
     without its paid extras). The key order keeps the documented order:
     children, then surcharge, then parent. A single line stays a plain change. */
  function batchRemoval(steps) {
    if (steps.length < 2) { return steps; }
    var updates = {};
    for (var i = 0; i < steps.length; i++) { updates[steps[i].key] = 0; }
    return [{ op: 'update', updates: updates, bundle: true, reason: 'remove-group' }];
  }

  /* ---- wedding date (stored attribute formats are DATA: never translated) ---- */

  function parseISO(iso) {
    if (!iso) { return null; }
    var p = String(iso).split('-');
    if (p.length !== 3) { return null; }
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d.getTime()) ? null : d;
  }

  /* "D Month YYYY" in en-GB whatever the page language: the stored value the
     order and the support system read. Same as britten-cart-wedding-date. */
  function humanDate(iso) {
    var d = parseISO(iso);
    if (!d) { return ''; }
    return d.getDate() + ' ' + d.toLocaleDateString('en-GB', { month: 'long' }) + ' ' + d.getFullYear();
  }

  /* The two cart attributes for a date. An empty or unusable date CLEARS both
     ("" removes an attribute on cart/update.js), as the product page checker
     does, so an abandoned date does not ride along to the order. */
  function weddingAttributes(iso) {
    var human = humanDate(iso);
    return human
      ? { 'Wedding date': human, 'Wedding date (ISO)': iso }
      : { 'Wedding date': '', 'Wedding date (ISO)': '' };
  }

  /* Is this input value a COMPLETE, sensible date to write to the cart? Chrome
     fires change while a year is still being typed ("0002-06-12"), and writing
     that stored "12 June 1902". Needs four digits, a real calendar day, and to sit
     inside the input's own min and max when it has them. */
  function isWritableDate(iso, min, max) {
    if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) { return false; }
    var d = parseISO(iso);
    if (!d) { return false; }
    var back = d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    if (back !== iso) { return false; }
    if (min && iso < min) { return false; }
    if (max && iso > max) { return false; }
    return true;
  }

  /* One serialised, debounced writer for cart/update.js (the wedding date and the
     order note share it). schedule(patch) merges into what is waiting (per attribute
     key; the note is replaced), so the LAST value wins; after `delay` ms it sends ONE
     request; requests go out one at a time in order, so keepalive writes never race
     each other; flush() sends what is waiting now and resolves when the line is clear.
     send(body) must return a promise (its failure is swallowed: a failed write must
     never stop the page). `timers` is for the tests. */
  function createUpdateWriter(send, delay, timers) {
    var set = (timers && timers.setTimeout) || setTimeout;
    var clear = (timers && timers.clearTimeout) || clearTimeout;
    var pending = null;
    var timer = null;
    var chain = Promise.resolve();
    var inFlight = 0;
    function merge(patch) {
      pending = pending || {};
      if (patch.attributes) {
        pending.attributes = pending.attributes || {};
        for (var k in patch.attributes) {
          if (Object.prototype.hasOwnProperty.call(patch.attributes, k)) { pending.attributes[k] = patch.attributes[k]; }
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'note')) { pending.note = patch.note; }
    }
    function fire() {
      timer = null;
      if (!pending) { return chain; }
      var body = pending;
      pending = null;
      inFlight++;
      chain = chain.then(function () { return send(body); }).then(function () { inFlight--; }, function () { inFlight--; });
      return chain;
    }
    return {
      schedule: function (patch) {
        merge(patch);
        if (timer) { clear(timer); }
        timer = set(fire, delay);
      },
      flush: function () {
        if (timer) { clear(timer); timer = null; }
        return fire();
      },
      hasWork: function () { return !!pending || inFlight > 0; }
    };
  }

  /* The date as the customer reads it, weekday and year included, in the page
     language. BrittenI18n.dateSingle returns '' for English (the caller then
     formats en-GB, as everywhere else in the theme). */
  function formatWeddingDate(iso, i18nObj) {
    var d = parseISO(iso);
    if (!d) { return ''; }
    var local = (i18nObj && typeof i18nObj.dateSingle === 'function') ? i18nObj.dateSingle(d, { year: true, weekday: true }) : '';
    if (local) { return local; }
    return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  /* ---- timing advice for the whole bag ----
     lines: [{ title, tags: [] }] for every PRIMARY piece. calc is
     window.BrittenDeliveryCalc ({ estimate(country, tags), weddingVerdict(est, iso) }),
     the one copy of the delivery maths. Returns { state, vars } where state is
       none       no date (nothing to say)
       good       every piece has a comfortable margin
       tight      standard is close; a faster service helps (vars.service, vars.dates)
       slow_item  exactly ONE piece misses, it has no rush option, and every other
                  piece makes the date on standard delivery (vars.product, vars.dates)
       late       the estimates do not fit (vars.dates = the latest arrival)
       unknown    no delivery data, an unusable date or a date in the past
     Verdict "ok" (standard arrives, but inside the 14-day buffer) is advised like
     "tight": the faster service is named when there is one, else unknown.
     Precedence is deterministic: unknown, then late or slow_item, then tight, then good. */
  function basketTiming(lines, iso, calc, country) {
    if (!iso) { return { state: 'none', vars: {} }; }
    if (!calc || typeof calc.estimate !== 'function' || typeof calc.weddingVerdict !== 'function' || !lines || !lines.length) {
      return { state: 'unknown', vars: {} };
    }
    var results = [];
    var i;
    for (i = 0; i < lines.length; i++) {
      var est = calc.estimate(country, lines[i].tags || []);
      var v = est ? calc.weddingVerdict(est, iso) : null;
      if (!est || v === null || v === 'past') { return { state: 'unknown', vars: {} }; }
      results.push({ line: lines[i], est: est, v: v });
    }
    var late = results.filter(function (r) { return r.v === 'late'; });
    var near = results.filter(function (r) { return r.v === 'tight' || r.v === 'ok'; });
    function arrival(r) {
      var tier = r.est.priority || r.est.standard;
      return tier && tier.maxISO ? tier.maxISO : '';
    }
    function latest(list, pick) {
      var best = list[0];
      for (var k = 1; k < list.length; k++) { if (pick(list[k]) > pick(best)) { best = list[k]; } }
      return best;
    }
    if (late.length) {
      /* "Your other pieces arrive in time" is only true when exactly ONE piece
         misses, it has no rush option, and every other piece makes the date on
         STANDARD delivery (good or ok). Two late pieces, or others that only
         make it with rush, are "late". */
      var onlySlow = late.length === 1 && !late[0].est.priority && results.length > 1;
      var othersOnStandard = results.every(function (r) { return r === late[0] || r.v === 'good' || r.v === 'ok'; });
      if (onlySlow && othersOnStandard) {
        return { state: 'slow_item', vars: { product: late[0].line.title, dates: late[0].est.standard.dates || '' } };
      }
      var worst = latest(results, arrival);
      var tier = worst.est.priority || worst.est.standard;
      return { state: 'late', vars: { dates: (tier && tier.dates) || '' } };
    }
    if (near.length) {
      var withRush = near.filter(function (r) { return !!r.est.priority; });
      if (!withRush.length) { return { state: 'unknown', vars: {} }; }
      var slowest = latest(withRush, function (r) { return r.est.priority.maxISO || ''; });
      return { state: 'tight', vars: { service: slowest.est.priority.label || '', dates: slowest.est.priority.dates || '' } };
    }
    return { state: 'good', vars: {} };
  }

  /* Which quantity boxes disagree with the last CONFIRMED cart, and what they should say.
     list: [{ key, value }] (what is on screen); intentFor(key) is the engine's pending wish
     for a line (undefined once applied or given up). A box with a live wish is left alone;
     every other box must read what the cart really holds. Used after every operation, so a
     change that failed or was abandoned never leaves an optimistic number on screen (the bag
     page's checkout form posts these boxes as updates[...]). */
  function confirmedQuantities(list, cart, intentFor) {
    var items = (cart && cart.items) || [];
    var byKey = {};
    var i;
    for (i = 0; i < items.length; i++) { byKey[items[i].key] = items[i].quantity; }
    var fixes = [];
    for (i = 0; i < (list || []).length; i++) {
      var key = list[i].key;
      if (intentFor && intentFor(key) !== undefined) { continue; }
      if (!Object.prototype.hasOwnProperty.call(byKey, key)) { continue; }
      if (byKey[key] !== list[i].value) { fixes.push({ key: key, value: byKey[key] }); }
    }
    return fixes;
  }

  /* The primary lines only, as sorted "key:quantity" pairs. The drawer carries no
     hidden inputs for child lines, so this is what it can compare with the screen. */
  function primarySignature(cart) {
    var groups = analyse(cart).groups;
    var out = [];
    for (var i = 0; i < groups.length; i++) { out.push(groups[i].key + ':' + groups[i].item.quantity); }
    return out.sort().join('|');
  }

  /* ---- the drawer ---- */

  /* The drawer's state, as a pure reducer so it can be tested without a DOM.
       open   is it showing
       mode   'bag' ("Your bag") or 'added' ("Added to your bag")
       addedKey  the line key of the piece just added (shown first) */
  function drawerReducer(state, action) {
    state = state || { open: false, mode: 'bag', addedKey: null };
    switch (action && action.type) {
      case 'open-bag':
        return { open: true, mode: 'bag', addedKey: null };
      case 'open-added':
        return { open: true, mode: action.key ? 'added' : 'bag', addedKey: action.key || null };
      case 'added-gone':
        return { open: state.open, mode: 'bag', addedKey: null };
      case 'close':
        return { open: false, mode: 'bag', addedKey: null };
      default:
        return state;
    }
  }

  /* Which primary group did the add create? A new line key; failing that (the same
     line grew because the same variant and properties were added again) the line whose
     quantity went up. null when neither can be told. */
  function findAddedKey(before, after) {
    var b = analyse(before);
    var a = analyse(after);
    var i;
    for (i = 0; i < a.groups.length; i++) {
      if (!b.byKey[a.groups[i].key]) { return a.groups[i].key; }
    }
    for (i = 0; i < a.groups.length; i++) {
      var prev = b.byKey[a.groups[i].key];
      if (prev && a.groups[i].item.quantity > prev.item.quantity) { return a.groups[i].key; }
    }
    return null;
  }

  /* The add-to-bag interception, as a flow over injected dependencies so every path
     can be tested. deps:
       readCart()      -> promise of the un-prefixed cart
       postAdd()       -> promise of { ok, status, error? }: the ONE ajax add of the real form
       afterAdd(after) -> promise of { ok, cart }: reconcile (surcharge etc.) and re-render.
                          `after` is the cart just read after the add (absent when that read
                          failed); the old view ignores it.
       openAdded(key, cart)     show the drawer "Added to your bag"
       openBag()                show the drawer as "Your bag"
       nativeSubmit()           the page's own submit, exactly as it would have gone
       sleep(ms)                (optional) a promise that resolves after ms; the tests pass an instant one
     FAST ADD (all optional; the view passes them only with britten_bag_fast_add on):
       fast            true: the drawer was opened in its loading state before this ran
       onAdded(r)      the add was accepted: r is postAdd's answer (it may carry the
                       drawer HTML that came back with the add)
       abandon()       close that loading drawer; called just before nativeSubmit
     Rules: the add is POSTED at most once; the native submit happens at most once and
     NEVER after an add that may have landed (that would add twice). Where we cannot
     tell whether it landed (cannot even read the cart), nothing is repeated and the
     drawer opens on the real bag. Returns { path, addedKey }. */
  function runAddFlow(deps) {
    var result = { path: '', addedKey: null };
    var fast = deps.fast === true;
    function native(path) {
      result.path = path;
      if (fast && deps.abandon) { deps.abandon(); }
      deps.nativeSubmit();
      return result;
    }

    function landed(before) {
      return deps.readCart().then(function (after) { return verified(before, after); }, function () {
        return deps.afterAdd().then(function (res) {
          result.path = 'drawer';
          deps.openAdded(null, res && res.cart);
          return result;
        });
      });
    }

    function verified(before, after) {
      var key = findAddedKey(before, after);
      result.addedKey = key;
      return deps.afterAdd(after).then(function (res) {
        result.path = 'drawer';
        deps.openAdded(key, (res && res.cart) || after);
        return result;
      });
    }

    return deps.readCart().then(function (before) {
      return deps.postAdd().then(function (r) {
        if (r && r.ok) {
          if (fast && deps.onAdded) {
            try { deps.onAdded(r); } catch (e) { /* view only: afterAdd renders the usual way */ }
          }
          return landed(before);
        }
        if (r && r.status >= 400 && r.status < 500) { return native('native-refused'); }
        /* A network error, a timeout or a 5xx: it may have landed, and an aborted request
           does not stop Shopify finishing it later. Look up to three times over about six
           seconds (now, +3s, +6s) before concluding it did not; only then the native add. */
        var waits = [0, 3000, 3000];
        var sleep = deps.sleep || function (ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); };
        function look(n) {
          return sleep(waits[n]).then(function () { return deps.readCart(); }).then(function (now) {
            if (cartSignature(now) !== cartSignature(before)) { return landed(before); }
            if (n + 1 < waits.length) { return look(n + 1); }
            return native('native-not-landed');
          }, function () {
            result.path = 'unsure';
            deps.openBag();
            return result;
          });
        }
        return look(0);
      });
    }, function () {
      /* nothing has been sent yet, so the native add is safe */
      return native('native-no-read');
    });
  }

  /* The checkout controls: every button[name="checkout"] (page and sticky bar)
     and the express wrapper. `locked` is the coordinator's own lock, a separate
     attribute from the veil limit's class, so the gate's un-gate can never open
     a bag that is still reconciling. Idempotent: it writes an attribute only if
     it differs, so a mutation observer calling it again causes no loop. Takes
     any element-like object (the tests pass a fake). */
  var GATE_BUTTON_CLASS = 'britten-veil-gate-disabled';
  var GATE_CONTAINER_CLASS = 'britten-veil-gate-container-disabled';

  function setAttrIfDifferent(el, name, value) {
    if (el.getAttribute(name) !== value) { el.setAttribute(name, value); }
  }

  function lockControls(scope, locked) {
    var buttons = scope.querySelectorAll('button[name="checkout"]');
    var wrappers = scope.querySelectorAll('.britten-cart-summary__additional');
    var i;
    for (i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      if (locked) {
        setAttrIfDifferent(b, 'data-britten-bag-locked', 'true');
        setAttrIfDifferent(b, 'aria-disabled', 'true');
        if (!b.hasAttribute('disabled')) { b.setAttribute('disabled', ''); }
      } else {
        if (b.hasAttribute('data-britten-bag-locked')) { b.removeAttribute('data-britten-bag-locked'); }
        if (!b.classList.contains(GATE_BUTTON_CLASS)) {
          if (b.hasAttribute('aria-disabled')) { b.removeAttribute('aria-disabled'); }
          if (b.hasAttribute('disabled')) { b.removeAttribute('disabled'); }
        }
      }
    }
    for (i = 0; i < wrappers.length; i++) {
      var w = wrappers[i];
      if (locked) {
        setAttrIfDifferent(w, 'data-britten-bag-locked', 'true');
        setAttrIfDifferent(w, 'aria-disabled', 'true');
        if (!w.hasAttribute('inert')) { w.setAttribute('inert', ''); }
      } else {
        if (w.hasAttribute('data-britten-bag-locked')) { w.removeAttribute('data-britten-bag-locked'); }
        if (!w.classList.contains(GATE_CONTAINER_CLASS)) {
          if (w.hasAttribute('inert')) { w.removeAttribute('inert'); }
          if (w.hasAttribute('aria-disabled')) { w.removeAttribute('aria-disabled'); }
        }
      }
    }
  }

  /* ======================================================================
     Engine: serial queue, mutations, reconciliation, lock state
     No DOM. All I/O goes through opts.fetch, so the tests can fake the shop.

     opts.fetch       fetch-compatible function
     opts.rootUrl     locale-prefixed shop root (Shopify.routes.root_url)
     opts.sectionId   section to bundle and re-render (cart__main)
     opts.sectionsUrl pathname sent as sections_url with bundled sections
     opts.origin      location.origin; every request URL is absolute
     opts.scaleIo     override for SCALE_IO_CHILDREN_WITH_PARENT (tests)
     opts.timeoutMs   per-request timeout (default REQUEST_TIMEOUT_MS)
     opts.hooks       { onState(snapshot), render(html, cart, meta),
                        onSettled(info), onFailure(err, meta),
                        signature() -> the cart signature of what is on screen,
                        signatureOf(cart) -> the signature to compare it with (default:
                        every line; the drawer compares primary lines only) }

     Every public method returns a promise that always RESOLVES, with
     { ok: boolean, error?: {...} }.
     ====================================================================== */

  function createEngine(opts) {
    var fetchImpl = opts.fetch;
    var rootUrl = normaliseRoot(opts.rootUrl);
    var sectionId = opts.sectionId || 'cart__main';
    var sectionsUrl = opts.sectionsUrl || '';
    var hooks = opts.hooks || {};
    var scaleIo = (opts.scaleIo === undefined) ? SCALE_IO_CHILDREN_WITH_PARENT : !!opts.scaleIo;
    var timeoutMs = opts.timeoutMs || REQUEST_TIMEOUT_MS;
    var origin = opts.origin || (root.location && root.location.origin) || '';

    var state = {
      pending: 0,
      unresolved: UNRESOLVED_AT_START,
      error: null,
      busy: {}
    };
    var queue = Promise.resolve();
    var pendingQty = {};
    var intent = {};
    var lastCart = null;       /* the last cart a read CONFIRMED */
    var mutations = 0;         /* bumped each time a cart-changing request is sent */
    var lastCartMark = 0;      /* `mutations` as it stood when the read behind lastCart began */

    /* A quantity wish is over once its operation ends, applied or given up, unless the
       customer has already queued a newer one. Otherwise a render after a failure would put
       the unconfirmed number back on screen. */
    function settleIntent(meta) {
      if (meta && meta.kind === 'quantity' && meta.key && !pendingQty[meta.key]) { delete intent[meta.key]; }
    }

    function snapshot() {
      var busy = {};
      var k;
      for (k in state.busy) {
        if (Object.prototype.hasOwnProperty.call(state.busy, k) && state.busy[k] > 0) { busy[k] = true; }
      }
      return {
        locked: state.pending > 0 || state.unresolved,
        pending: state.pending,
        unresolved: state.unresolved,
        error: state.error,
        busy: busy
      };
    }

    function emit() {
      if (hooks.onState) {
        try { hooks.onState(snapshot()); } catch (e) { /* a view error must never stop the queue */ }
      }
    }

    /* ---- requests ---- */

    function request(url, init) {
      var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = null;
      if (controller) {
        init.signal = controller.signal;
        timer = setTimeout(function () { controller.abort(); }, timeoutMs);
      }
      function netError(err) {
        if (timer) { clearTimeout(timer); }
        var e = new Error('network');
        e.code = 'network';
        e.cause = err;
        return e;
      }
      var pending;
      try {
        pending = fetchImpl(url, init);   /* a wrapper may throw before it returns */
      } catch (err) {
        return Promise.reject(netError(err));
      }
      return Promise.resolve(pending).then(function (res) {
        if (timer) { clearTimeout(timer); }
        return res;
      }, function (err) {
        throw netError(err);
      });
    }

    function parseBody(res) {
      return res.text().then(function (text) {
        if (!text) { return null; }
        try { return JSON.parse(text); } catch (e) { return null; }
      });
    }

    function httpError(res, data) {
      var e = new Error('http ' + res.status);
      e.code = 'http';
      e.status = res.status;
      e.data = data || null;
      return e;
    }

    /* The cart read is UN-prefixed on purpose: titles and handles read from it
       are written into order properties, which stay English. */
    function readCart() {
      var mark = mutations;
      return request(buildUrl('/', 'cart.js', origin), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        return parseBody(res).then(function (data) {
          if (!res.ok || !data || !Array.isArray(data.items)) { throw httpError(res, data); }
          lastCart = data;
          lastCartMark = mark;
          return data;
        });
      });
    }

    /* `immune` sends the same JSON bytes inside a Blob. snippets/britten-norefunds.liquid
       wraps window.fetch on norefunds product pages and rewrites any request to
       /cart/add whose body is a STRING (adds properties.Returns) or a FormData. A Blob is
       neither, so the surcharge line is posted exactly as the old cart page posted it. */
    function post(path, body, immune) {
      mutations++;
      var json = JSON.stringify(body);
      var payload = json;
      if (immune && typeof Blob !== 'undefined') {
        try { payload = new Blob([json], { type: 'application/json' }); } catch (e) { payload = json; }
      }
      return request(buildUrl(rootUrl, path, origin), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: payload
      }).then(function (res) {
        return parseBody(res).then(function (data) {
          if (!res.ok) { throw httpError(res, data); }
          return data;
        });
      });
    }

    function getSections() {
      return request(buildUrl(rootUrl, '', origin, 'sections=' + encodeURIComponent(sectionId)), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        return parseBody(res).then(function (data) {
          if (!res.ok) { throw httpError(res, data); }
          return (data && data[sectionId]) || null;
        });
      });
    }

    /* ---- steps ---- */

    function execStep(step, ctx) {
      ctx.mutated = true;
      if (step.op === 'add') {
        /* No section bundling here: the body must stay byte-identical to the
           payload addMissingSurcharges always sent. */
        ctx.sections = null;
        return post('cart/add.js', step.payload, true);
      }
      var body = step.op === 'update' ? { updates: step.updates } : { id: step.key, quantity: step.quantity };
      var bundle = step.bundle === true;
      if (bundle) {
        body.sections = sectionId;
        if (sectionsUrl) { body.sections_url = sectionsUrl; }
      }
      ctx.sections = null;
      return post(step.op === 'update' ? 'cart/update.js' : 'cart/change.js', body).then(function (data) {
        if (bundle && data && data.sections && data.sections[sectionId]) {
          ctx.sections = data.sections[sectionId];
        }
        return data;
      });
    }

    function runSteps(steps, ctx) {
      var chain = Promise.resolve();
      steps.forEach(function (step) {
        chain = chain.then(function () { return execStep(step, ctx); });
      });
      return chain;
    }

    function integrityError(problems) {
      var e = new Error('integrity');
      e.code = 'integrity';
      e.problems = problems;
      return e;
    }

    /* Read, plan, fix, repeat until the plan is empty. The plan always comes
       from a fresh read, so a retry after a timeout can never add twice.
       The one exception is ctx.seed (the fast add): a cart the caller JUST read. It
       stands in for the first read, and only while it is still the engine's latest
       read and no cart-changing request has been sent since that read began. Every
       later pass reads again. */
    function seedFor(ctx) {
      var seed = ctx.seed;
      ctx.seed = null;
      return (seed && seed === lastCart && lastCartMark === mutations) ? seed : null;
    }

    function reconcile(ctx) {
      var pass = 0;
      function loop() {
        pass++;
        var seeded = pass === 1 ? seedFor(ctx) : null;
        return (seeded ? Promise.resolve(seeded) : readCart()).then(function (cart) {
          ctx.cart = cart;
          var plan = planReconcile(cart);
          if (plan.steps.length === 0) {
            if (plan.problems.length) { throw integrityError(plan.problems); }
            return cart;
          }
          if (pass > MAX_RECONCILE_PASSES) {
            throw integrityError([{ code: 'not-converging' }]);
          }
          return runSteps(plan.steps, ctx).then(loop);
        });
      }
      return loop();
    }

    function render(ctx, meta) {
      var need = ctx.mutated || ctx.force || meta.forceRender;
      if (!need) { return Promise.resolve(); }
      var htmlPromise = ctx.sections ? Promise.resolve(ctx.sections) : getSections();
      return htmlPromise.then(function (html) {
        if (!html) {
          var e = new Error('render');
          e.code = 'render';
          throw e;
        }
        if (hooks.render) { hooks.render(html, ctx.cart, meta); }
      });
    }

    /* ---- one queued operation ---- */

    function runOp(meta, body) {
      state.pending++;
      if (meta.key) { state.busy[meta.key] = (state.busy[meta.key] || 0) + 1; }
      emit();

      var ctx = { sections: null, cart: null, mutated: false, force: false };

      function settle() {
        return reconcile(ctx).then(function () { return render(ctx, meta); });
      }

      function done(result) {
        settleIntent(meta);
        state.pending--;
        if (meta.key) { state.busy[meta.key] = Math.max(0, (state.busy[meta.key] || 1) - 1); }
        emit();
        return result;
      }

      return Promise.resolve().then(function () { return body(ctx); })
        .then(settle)
        .then(function () {
          state.unresolved = false;
          state.error = null;
          if (hooks.onSettled) {
            try { hooks.onSettled({ meta: meta, cart: ctx.cart, mutated: ctx.mutated }); } catch (e) { /* view only */ }
          }
          return done({ ok: true, cart: ctx.cart });
        }, function (err) {
          settleIntent(meta);
          state.error = { code: err && err.code ? err.code : 'error', detail: err && err.problems ? err.problems : null };
          state.unresolved = true;
          emit();
          if (hooks.onFailure) {
            try { hooks.onFailure(err, meta); } catch (e) { /* view only */ }
          }
          /* One quiet recovery attempt: re-read the real cart and re-plan from
             it. This never repeats an add blindly. If it works the view is true
             again and checkout opens, but the error stays on screen until the
             next successful change. */
          var recoveryCtx = { sections: null, cart: null, mutated: false, force: false };
          var recoveryMeta = { kind: 'recover', forceRender: true };
          return reconcile(recoveryCtx).then(function () {
            return render(recoveryCtx, recoveryMeta);
          }).then(function () {
            state.unresolved = false;
            if (hooks.onSettled) {
              try { hooks.onSettled({ meta: recoveryMeta, cart: recoveryCtx.cart, mutated: true }); } catch (e) { /* view only */ }
            }
            return done({ ok: false, error: state.error });
          }, function () {
            return done({ ok: false, error: state.error });
          });
        });
    }

    function enqueue(task) {
      var p = queue.then(task).catch(function (err) {
        /* Last line of defence: a bug in the code above must still end in the
           documented error state, never an unhandled rejection. */
        state.error = { code: 'error', detail: null };
        state.unresolved = true;
        emit();
        return { ok: false, error: state.error };
      });
      queue = p.then(function () {}, function () {});
      return p;
    }

    /* ---- public operations ---- */

    function setQuantity(key, quantity) {
      var entry = pendingQty[key];
      intent[key] = quantity;
      if (entry && !entry.started) {
        entry.target = quantity;
        return entry.promise;
      }
      entry = { target: quantity, started: false, promise: null };
      pendingQty[key] = entry;
      state.pending++;
      state.busy[key] = (state.busy[key] || 0) + 1;
      emit();
      entry.promise = enqueue(function () {
        entry.started = true;
        if (pendingQty[key] === entry) { delete pendingQty[key]; }
        /* Release the queue-time hold; runOp takes its own. */
        state.pending--;
        state.busy[key] = Math.max(0, (state.busy[key] || 1) - 1);
        return runOp({ kind: 'quantity', key: key }, function (ctx) {
          return readCart().then(function (cart) {
            ctx.cart = cart;
            var target = entry.target;
            if (target <= 0) { return runSteps(batchRemoval(planRemoveGroup(cart, key)), ctx); }
            return runSteps(planQuantityChange(cart, key, target, { scaleIo: scaleIo }), ctx);
          });
        }).then(function (res) {
          /* The newest tap is the customer's intent until it has been applied. */
          if (!pendingQty[key]) { delete intent[key]; }
          return res;
        });
      });
      return entry.promise;
    }

    function removeGroup(key) {
      state.pending++;
      state.busy[key] = (state.busy[key] || 0) + 1;
      emit();
      return enqueue(function () {
        state.pending--;
        state.busy[key] = Math.max(0, (state.busy[key] || 1) - 1);
        return runOp({ kind: 'remove', key: key }, function (ctx) {
          return readCart().then(function (cart) {
            ctx.cart = cart;
            return runSteps(batchRemoval(planRemoveGroup(cart, key)), ctx);
          });
        });
      });
    }

    /* The drawer's add: POST the REAL product form (FormData, so every property, the
       option fields, the norefunds input and selling-plan fields go as the
       form holds them) to {root}cart/add.js, once, in the queue. Never throws and never
       retries: it resolves { ok, status, data } or { ok:false, status:0, error }.
       opts.bundle (the fast add) also asks Shopify for the drawer section in the same
       response, the way change.js and update.js do: the answer then carries
       data.sections[sectionId]. Only two extra fields are added to the form data. */
    function addRaw(body, opts) {
      var bundle = !!(opts && opts.bundle);
      /* A plain object ({ items: [...] } from our product options) goes as JSON;
         the product form's FormData goes as it is. */
      var json = !!body && typeof body === 'object' && typeof body.set !== 'function' && typeof body.append !== 'function';
      state.pending++;
      emit();
      return enqueue(function () {
        if (bundle && json) {
          body.sections = sectionId;
          if (sectionsUrl) { body.sections_url = sectionsUrl; }
        } else if (bundle && body && typeof body.set === 'function') {
          body.set('sections', sectionId);
          if (sectionsUrl) { body.set('sections_url', sectionsUrl); }
        }
        mutations++;
        return request(buildUrl(rootUrl, 'cart/add.js', origin), {
          method: 'POST',
          credentials: 'same-origin',
          headers: json ? { 'Accept': 'application/json', 'Content-Type': 'application/json' } : { 'Accept': 'application/json' },
          body: json ? JSON.stringify(body) : body
        }).then(function (res) {
          return parseBody(res).then(function (data) { return { ok: res.ok, status: res.status, data: data }; });
        }, function (err) {
          return { ok: false, status: 0, error: err };
        }).then(function (r) {
          state.pending--;
          emit();
          return r;
        });
      });
    }

    /* Add one product (the accessory offer). Same body the old upsell sent,
       through the same queue, so the reconcile and the re-render follow. */
    function addItem(variantId, quantity) {
      state.pending++;
      emit();
      return enqueue(function () {
        state.pending--;
        return runOp({ kind: 'add-item' }, function (ctx) {
          ctx.mutated = true;
          ctx.sections = null;
          return post('cart/add.js', { items: [{ id: variantId, quantity: quantity || 1 }] });
        });
      });
    }

    /* The quantity the customer last asked for on a line, while it is still
       queued or in flight; undefined once applied. The view reads it so a
       section swap cannot overwrite taps that have not landed yet. */
    function intentFor(key) {
      return Object.prototype.hasOwnProperty.call(intent, key) ? intent[key] : undefined;
    }

    /* Is what is on screen still the cart? Reads the cart and compares it with
       hooks.signature(); if they differ the section is re-rendered. Used when a
       page comes back from the back/forward cache and when the tab regains
       focus. */
    function verify() {
      state.pending++;
      emit();
      return enqueue(function () {
        state.pending--;
        return runOp({ kind: 'verify' }, function (ctx) {
          return readCart().then(function (cart) {
            ctx.cart = cart;
            var sig = hooks.signatureOf ? hooks.signatureOf(cart) : cartSignature(cart);
            if (hooks.signature && hooks.signature() !== sig) { ctx.force = true; }
          });
        });
      });
    }

    /* Re-read, reconcile and re-render (forced). Used after an add made by
       another script and when a page comes back from the back/forward cache. */
    function refresh() {
      state.pending++;
      emit();
      return enqueue(function () {
        state.pending--;
        return runOp({ kind: 'refresh', forceRender: true }, function () { return Promise.resolve(); });
      });
    }

    /* The second half of the fast add. `cart` is the read the caller took right after
       a good add; it is the reconcile's first pass (see seedFor). opts.rendered says
       the caller has already put the section that came back WITH the add on screen:
       then nothing is fetched or swapped unless the reconcile changed the bag, or the
       screen disagrees with the cart (the same test verify() uses). Without it the
       section is fetched and swapped, as refresh() does. */
    function settleAdd(cart, opts) {
      var rendered = !!(opts && opts.rendered);
      state.pending++;
      emit();
      return enqueue(function () {
        state.pending--;
        return runOp({ kind: 'refresh', forceRender: !rendered }, function (ctx) {
          ctx.seed = cart;
          if (rendered && hooks.signature) {
            var sig = hooks.signatureOf ? hooks.signatureOf(cart) : cartSignature(cart);
            if (hooks.signature() !== sig) { ctx.force = true; }
          }
          return Promise.resolve();
        });
      });
    }

    /* The check made when the page loads. Renders only if it had to change
       something. */
    function start() {
      return enqueue(function () {
        return runOp({ kind: 'init' }, function () { return Promise.resolve(); });
      });
    }

    function retry() {
      return refresh();
    }

    function whenIdle() {
      return queue.then(function () {});
    }

    return {
      start: start,
      refresh: refresh,
      settleAdd: settleAdd,
      retry: retry,
      setQuantity: setQuantity,
      removeGroup: removeGroup,
      verify: verify,
      confirmedCart: function () { return lastCart; },
      addItem: addItem,
      addRaw: addRaw,
      readCart: readCart,
      intentFor: intentFor,
      whenIdle: whenIdle,
      getState: snapshot
    };
  }

  var api = {
    SCALE_IO_CHILDREN_WITH_PARENT: SCALE_IO_CHILDREN_WITH_PARENT,
    getProp: getProp,
    hasCustomLengthProp: hasCustomLengthProp,
    owesLengthSurcharge: owesLengthSurcharge,
    isSurchargeLine: isSurchargeLine,
    isIoChildLine: isIoChildLine,
    analyse: analyse,
    displayCount: displayCount,
    overageOf: overageOf,
    surchargeQuantity: surchargeQuantity,
    buildSurchargeAddPayload: buildSurchargeAddPayload,
    planReconcile: planReconcile,
    planRemoveGroup: planRemoveGroup,
    planQuantityChange: planQuantityChange,
    scaleChildQuantity: scaleChildQuantity,
    normaliseRoot: normaliseRoot,
    buildUrl: buildUrl,
    cartSignature: cartSignature,
    veilUnits: veilUnits,
    batchRemoval: batchRemoval,
    lockControls: lockControls,
    primarySignature: primarySignature,
    confirmedQuantities: confirmedQuantities,
    drawerReducer: drawerReducer,
    findAddedKey: findAddedKey,
    runAddFlow: runAddFlow,
    parseISO: parseISO,
    isWritableDate: isWritableDate,
    createUpdateWriter: createUpdateWriter,
    humanDate: humanDate,
    weddingAttributes: weddingAttributes,
    formatWeddingDate: formatWeddingDate,
    basketTiming: basketTiming,
    createEngine: createEngine
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof root.document !== 'undefined') { root.BrittenBagCoordinator = api; }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
